import { MutableRefObject, Suspense, useContext, useEffect, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { History } from "history";
import { TFunction, useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { wrapPromise } from "go-core";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { ProductPackageApi } from "../../../../services/types";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "PRODUCT_PACKAGE";
const resourceType = "PRODUCT_PACKAGE";

const buildConfig = (
	data: Record<string, any>,
	t: TFunction,
	segmentListService: SegmentListService,
	history: History,
	onRemove: (itemId: number) => void,
	listParamsRef: MutableRefObject<Record<string, any>>
) => {
	let config = {
		fields: [
			{
				id: "name",
				name: t("common.word.name", { ns: "lib" }),
				type: "text",
			},
			{
				id: "measure_type",
				name: t("common.word.measure_type"),
				type: "list",
				listOptions: {
					ITEM: t("enums.products.measure_types.ITEM"),
					KILOGRAM: t("enums.products.measure_types.KILOGRAM"),
					LITER: t("enums.products.measure_types.LITER"),
					PACKAGE: t("enums.products.measure_types.PACKAGE"),
				},
				render: (item: ProductPackageApi) => t(`enums.products.measure_types.${item.measure_type}`),
			},
			{
				id: "specific_weight",
				name: t("modules.product_package.field.specific_weight.title"),
			},
			{
				id: "barcodes",
				name: t("modules.product_package.field.barcodes.title"),
				render: (item: ProductPackageApi) =>
					item.barcodes.map((productPackageBarcode) => {
						const { id, barcode, net_weight, volume } = productPackageBarcode;
						return (
							<div key={id} className="mb-2">
								<div>
									<span>{`${t("common.word.barcode")}: `}</span>
									<span>{barcode}</span>
								</div>
								<div>
									<span>{`${t("modules.product_package.field.net_weight.title")}: `}</span>
									<span>{net_weight}</span>
								</div>
								<div>
									<span>{`${t("common.word.volume")}: `}</span>
									<span>{volume}</span>
								</div>
							</div>
						);
					}),
				renderExport: (item: ProductPackageApi) => {
					return item.barcodes
						.map((productPackageBarcode) => {
							const { barcode, net_weight, volume } = productPackageBarcode;
							return `${t("common.word.barcode")}: ${barcode} ${t(
								"modules.product_package.field.net_weight.title"
							)}: ${net_weight} ${t("common.word.volume")}: ${volume}`;
						})
						.join(", \n");
				},
				disableSorting: true,
			},
		],
		actions: [
			{
				name: t("common.action.edit", { ns: "lib" }),
				click: (item: ProductPackageApi) => history.push(`/admin/product_packages/${item.id}/edit`),
			},
			{
				name: t("common.action.remove", { ns: "lib" }),
				click: (item: ProductPackageApi) => onRemove(item.id),
				refreshListAfter: true,
			},
		],
		selectedColumns: ["name", "measure_type", "specific_weight", "barcodes"],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		filters: [
			{
				id: "barcode",
				name: t("common.word.barcode"),
				type: "text",
			},
		],
		exportConfig: {
			title: t("modules.product_package.header.title"),
			filename: t("modules.product_package.field.export_config.file_name"),
		},
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "barcodes";
			listParamsRef.current = params;
			return adminApi.getProductPackages(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => {
			return segmentListService.save(listName, resourceType, segment);
		},
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return config;
};
const List = ({ resource }: ListState) => {
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const segmentContextValue = useContext(SegmentContext);
	const path = "/admin/product_packages";
	const isMobile = useWindowSize().isMobile;
	const { addSuccessFlash } = useFlash();
	const confirmation = useConfirmation();
	const listParamsRef = useRef<Record<string, any>>({});

	if (!resource) return null;
	const data = resource.read();

	const buttons: ButtonProps[] = [
		{ title: t("common.action.add", { ns: "lib" }), path: `${path}/new`, variant: "primary" },
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `${path}/new`,
		},
	];

	const onRemove = async (itemId: number) => {
		await confirmation({
			title: t("confirmation.title", { ns: "lib" }),
			message: t("confirmation.message.remove", { ns: "lib" }),
		});
		await adminApi.deleteProductPackage(itemId);
		addSuccessFlash(t("common.flash.removed", { ns: "lib" }));
	};

	const config = buildConfig(data, t, segmentContextValue, history, onRemove, listParamsRef);

	return (
		<>
			{!isMobile && <Header title={t("modules.product_package.header.title")} buttons={buttons} />}
			<ListData
				config={config}
				emptyList={{ addAction: () => history.push(`${location.pathname}/new`) }}
				mobileActions={mobileActions}
			/>
		</>
	);
};

export const ProductPackagesIndexPage = () => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { t } = useTranslation();

	useEffect(() => {
		handleChangeTabTitle(t("modules.product_package.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<List resource={resource} />
		</Suspense>
	);
};
